import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollLockMethods {
  lockScroll: (id: string) => void;
  unlockScroll: (id: string) => void;
  isScrollLockActive: () => boolean;
}

const getScrollbarWidth = async () => {
  const outer = document.createElement('div');
  const inner = document.createElement('div');

  outer.style.overflowY = 'scroll';
  outer.style.height = '100px';
  outer.style.width = '100px';
  inner.style.height = '200px';

  outer.appendChild(inner);
  document.body.appendChild(outer);

  // Wait until scrollbar is rendered
  await new Promise((resolve) => {
    setTimeout(resolve);
  });

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  document.body.removeChild(outer);

  return scrollbarWidth;
};

export const useScrollLockManager = (): ScrollLockMethods => {
  const ids = useRef(new Set<string>());

  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    void getScrollbarWidth().then(setScrollbarWidth);
  }, []);

  const calculateScrollLock = useCallback(
    (isEmpty: boolean) => {
      if (isEmpty) {
        document.body.style.overflow = 'auto';

        document.body.style.removeProperty('padding-right');
      } else {
        document.body.style.overflow = 'hidden';

        const isContentBiggerThanViewport = document.body.scrollHeight > window.innerHeight;
        if (isContentBiggerThanViewport) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
      }
    },
    [scrollbarWidth],
  );

  const lockScroll = useCallback<ScrollLockMethods['lockScroll']>(
    (id) => {
      ids.current.add(id);
      calculateScrollLock(ids.current.size === 0);
    },
    [calculateScrollLock],
  );
  const unlockScroll = useCallback<ScrollLockMethods['unlockScroll']>(
    (id) => {
      ids.current.delete(id);
      calculateScrollLock(ids.current.size === 0);
    },
    [calculateScrollLock],
  );
  const isScrollLockActive = useCallback<ScrollLockMethods['isScrollLockActive']>(
    () => ids.current.size > 0,
    [],
  );

  const handleResize = useCallback(() => {
    calculateScrollLock(ids.current.size === 0);
  }, [calculateScrollLock]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    calculateScrollLock(true);
  }, [calculateScrollLock]);

  return {
    lockScroll,
    unlockScroll,
    isScrollLockActive,
  };
};
